import { z } from 'zod';

export const ProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).default(''),
  ownerId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const ProjectVersionStatusSchema = z.enum(['draft', 'review', 'published']);
export type ProjectVersionStatus = z.infer<typeof ProjectVersionStatusSchema>;

export const ProjectVersionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  versionLabel: z.string().min(1).max(40), // e.g. "1.0", "2.0-beta"
  status: ProjectVersionStatusSchema,
  notes: z.string().max(4000).default(''),
  createdFromVersionId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
});
export type ProjectVersion = z.infer<typeof ProjectVersionSchema>;

export const CreateProjectRequestSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

export const PatchProjectRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).optional(),
});
export type PatchProjectRequest = z.infer<typeof PatchProjectRequestSchema>;

export const TTS_VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'] as const;
export const TtsVoiceSchema = z.enum(TTS_VOICES);
export type TtsVoice = z.infer<typeof TtsVoiceSchema>;

// Bare domain, no protocol/path — e.g. "waterschaplimburg.nl".
const DOMAIN_RE = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
export const EmailDomainSchema = z.string().toLowerCase().regex(DOMAIN_RE);

// Never includes the key itself — only whether one is set.
export const ProjectSettingsSchema = z.object({
  hasOpenaiKey: z.boolean(),
  ttsVoice: TtsVoiceSchema,
  // Self-registrations with an email on one of these domains are
  // auto-approved and auto-enrolled into this project — see O1 in
  // docs/ontwerp.md, resolved this way rather than a generic enrollment UI.
  allowedEmailDomains: z.array(EmailDomainSchema).default([]),
});
export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;

export const PatchProjectSettingsRequestSchema = z.object({
  openaiKey: z.string().min(1).max(500).nullable().optional(),
  ttsVoice: TtsVoiceSchema.optional(),
  allowedEmailDomains: z.array(EmailDomainSchema).optional(),
});
export type PatchProjectSettingsRequest = z.infer<typeof PatchProjectSettingsRequestSchema>;
